import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Dropdown } from 'react-bootstrap';

import { formatDeviceSizeRange } from 'utils/Devices';
import { ChevronDownIcon } from 'assets/icons';

interface CuffSizeSelectorProps {
  options: IDeviceOrderingOption[];
  selectedSku: string;
  onSelect: (sku: string) => void;
  recommendedSku?: string;
  notice?: React.ReactNode;
}

export const CuffSizeSelector = ({
  options,
  selectedSku,
  onSelect,
  recommendedSku,
  notice
}: CuffSizeSelectorProps) => {
  const intl = useIntl();
  const [show, setShow] = useState(false);
  const sorted = [...options].sort((a, b) => a.lineOrder - b.lineOrder);
  const selected = sorted.find((o) => o.sku === selectedSku) ?? sorted[0];

  return (
    <div className="cuff-size-selector rounded p-3" onClick={(e) => e.stopPropagation()}>
      <p className="small fw-semibold mb-0">{intl.formatMessage({ id: 'selectCuffSizeKey' })}</p>
      <p className="text-secondary small mb-2">{intl.formatMessage({ id: 'cuffSizeHintKey' })}</p>

      {notice}

      <Dropdown show={show} onToggle={setShow}>
        <Dropdown.Toggle
          as="button"
          type="button"
          className="cuff-selector-trigger w-100 d-flex align-items-center justify-content-between bg-white border rounded"
        >
          <span className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-semibold text-dark">
              {selected.sku} {intl.formatMessage({ id: selected.displayName })}
            </span>
            <span className="text-secondary small">{formatDeviceSizeRange(selected.details!)}</span>
            {!selected.isDefaultAccessory && (
              <span className="text-secondary small" data-test="cuff-add-on">
                {intl.formatMessage({ id: 'cuffAddOnKey' })}
              </span>
            )}
            {selected.sku === recommendedSku && (
              <span className="badge-bmi-recommendation">
                {intl.formatMessage({ id: 'cuffBmiRecommendationKey' })}
              </span>
            )}
          </span>
          <ChevronDownIcon
            className="icon text-secondary ms-1 flex-shrink-0"
            style={{ fontSize: '12px' }}
          />
        </Dropdown.Toggle>

        <Dropdown.Menu className="w-100 cuff-selector-dropdown">
          {sorted.map((option) => (
            <button
              key={option.sku}
              type="button"
              className={`cuff-selector-option w-100 text-start d-flex align-items-center gap-2 px-3 py-2 rounded-0 border-0 ${
                option.sku === selectedSku ? 'bg-light' : 'bg-white'
              }`}
              onClick={() => {
                onSelect(option.sku);
                setShow(false);
              }}
            >
              <span className="fw-semibold text-dark">
                {option.sku} {intl.formatMessage({ id: option.displayName })}
              </span>
              <span className="text-secondary small">{formatDeviceSizeRange(option.details!)}</span>
              {/* The default cuff comes with the monitor and the others are invoiced on top
                  (Charles, 2026-09-11). Choosing one used to cost nothing and say nothing; now it
                  costs something, so it has to say so. */}
              {!option.isDefaultAccessory && (
                <span className="text-secondary small" data-test={`cuff-add-on-${option.sku}`}>
                  {intl.formatMessage({ id: 'cuffAddOnKey' })}
                </span>
              )}
              {option.sku === recommendedSku && (
                <span className="badge-bmi-recommendation ms-auto">
                  {intl.formatMessage({ id: 'cuffBmiRecommendationKey' })}
                </span>
              )}
            </button>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};
